// "What changed this period?" — factual comparison of the latest statement
// against the previous one. Uses the grounded client-side diff (computeCompare):
// when the backend can't classify a change we show the line-item difference,
// never a speculative explanation.
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GitCompareArrows, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { PayslipDetail } from '../types';
import { computeCompare } from '../lib/compare';
import { CATEGORY_META, formatMoney, MASK } from '../lib/format';

interface WhatChangedCardProps {
  latest?: PayslipDetail;
  previous?: PayslipDetail;
  loading: boolean;
  revealed: boolean;
  onCompare: () => void;
}

export const WhatChangedCard: React.FC<WhatChangedCardProps> = ({ latest, previous, loading, revealed, onCompare }) => {
  const { t } = useTranslation('payroll');
  const cmp = useMemo(
    () => (latest && previous ? computeCompare(latest, previous) : null),
    [latest, previous],
  );

  if (loading) return <Skeleton className="h-40 w-full rounded-xl" />;
  if (!cmp || !latest || !previous) return null; // first statement — nothing to compare

  const money = (n: number) => (revealed ? formatMoney(Math.abs(n), cmp.currency) : MASK);
  const movers = cmp.componentDeltas.slice(0, 4);

  return (
    <section aria-labelledby="mypay-changed">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 id="mypay-changed" className="flex items-center gap-2 text-h3 text-charcoal">
          <GitCompareArrows className="h-5 w-5 text-[#D9711E]" />
          {t('myPay.whatChangedTitle')}
        </h2>
        <Button variant="ghost" size="sm" onClick={onCompare}>{t('myPay.compareFull')}</Button>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        {t('myPay.whatChangedVs', { current: latest.periodLabel, previous: previous.periodLabel })}
      </p>

      {movers.length === 0 ? (
        <p className="rounded-xl border border-border px-4 py-6 text-center text-sm text-muted-foreground">
          {t('myPay.noMaterialChanges')}
        </p>
      ) : (
        <ul className="space-y-1">
          {movers.map((c) => {
            const up = c.deltaAmount > 0;
            const flat = Math.abs(c.deltaAmount) < 0.005;
            return (
              <li key={c.code} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
                <span className="flex items-center gap-2 text-charcoal">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CATEGORY_META[c.componentType].color }} aria-hidden />
                  {c.label}
                  <span className="text-[11px] text-muted-foreground">{t(CATEGORY_META[c.componentType].labelKey)}</span>
                </span>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 text-xs font-semibold tabular-nums',
                    flat ? 'text-muted-foreground' : up ? 'text-[#166534] dark:text-[#86efac]' : 'text-[#991B1B] dark:text-[#fca5a5]',
                  )}
                >
                  {flat ? <Minus className="h-3 w-3" /> : up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  <span className="sr-only">{flat ? t('myPay.noChange') : up ? t('myPay.up') : t('myPay.down')}</span>
                  {up ? '+' : '−'}{money(c.deltaAmount)}
                  {c.deltaPct != null && <span className="text-muted-foreground">({Math.abs(c.deltaPct).toFixed(1)}%)</span>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
